"use client";

import axios from "axios";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Field,
  FieldLabel,
  Input,
  NativeSelect,
} from "@stocks/ui";
import {
  ITEM_CATEGORY_VALUES,
  type ItemImageSearchResult,
  type StockInResultResponse,
} from "@stocks/shared";

import { useItemDraftStore } from "@/shared/libs/zustand/stores/itemDraft.store";
import {
  EMPTY_ITEM_FORM,
  ITEM_CATEGORY_LABELS,
  ItemFormFields,
  toCreateItemRequest,
  useItem,
  type ItemFormValues,
} from "@/entities/item";
import { formatQuantity } from "@/entities/stock";
import { useCreateItem } from "@/features/item/create-item";
import { ImagePicker, useImagePicker } from "@/features/item/image-picker";
import {
  ItemRecommendations,
  useSearchItemsByImage,
} from "@/features/item/search-by-image";
import { useUpdateItem } from "@/features/item/update-item";
import { useUploadItemImage } from "@/features/item/upload-image";

import { emptyStockInForm, type StockInFormValues } from "../lib/stockInForm";
import { ReceiveForm } from "./ReceiveForm";

type Step = "find" | "new-item" | "receive" | "done";

const STEPS = [
  { key: "find", label: "사진·품목 선택" },
  { key: "receive", label: "입고 정보" },
  { key: "done", label: "완료" },
] as const;

const currentIndex = (step: Step) =>
  step === "find" || step === "new-item" ? 0 : step === "receive" ? 1 : 2;

const StepIndicator = ({ step }: { step: Step }) => (
  <ol className='flex flex-wrap items-center gap-2 text-sm'>
    {STEPS.map((s, i) => {
      const active = i === currentIndex(step);
      return (
        <li
          key={s.key}
          aria-current={active ? "step" : undefined}
          className={`flex items-center gap-2 rounded-full border px-3 py-1 ${
            active
              ? "border-primary bg-primary text-primary-foreground"
              : i < currentIndex(step)
                ? "text-foreground"
                : "text-muted-foreground"
          }`}
        >
          <span className='font-semibold'>{i + 1}</span>
          {s.label}
        </li>
      );
    })}
  </ol>
);

/**
 * 입고 화면 (사진 첨부 → 카테고리 → 추천 → 품목 선택/신규 등록 → 입고 입력 → 확정).
 * 사진·카테고리·입력값은 단계 이동 중에도 유지되고, 확정 전 이탈 시 경고한다.
 */
export const StockIn = ({ initialItemId }: { initialItemId?: string }) => {
  const draft = useItemDraftStore();
  const picker = useImagePicker(draft.files);
  const itemForm = useForm<ItemFormValues>({
    defaultValues: {
      ...EMPTY_ITEM_FORM,
      category: draft.category,
      spec: draft.spec,
    },
  });
  const receiveForm = useForm<StockInFormValues>({
    defaultValues: emptyStockInForm(),
  });

  const [step, setStep] = useState<Step>(initialItemId ? "receive" : "find");
  const [itemId, setItemId] = useState<string | null>(initialItemId ?? null);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [result, setResult] = useState<StockInResultResponse | null>(null);

  const search = useSearchItemsByImage();
  const createItem = useCreateItem();
  const upload = useUploadItemImage();
  const updateItem = useUpdateItem({ silent: true });
  const uploadingRef = useRef(false);
  const { data: item, isLoading: itemLoading, isError: itemError } = useItem(
    itemId ?? "",
  );

  const { images, primaryId } = picker;
  const hasFailed = images.some((i) => i.status === "error");
  const isBusy =
    createItem.isPending || uploadingRef.current || upload.isPending;
  const locked = createdId !== null;

  // 확정 전 입력이 남아 있으면 새로고침/탭 닫기 경고
  const dirty =
    step !== "done" &&
    (receiveForm.formState.isDirty || images.length > 0 || locked);
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const selectItem = (id: string) => {
    // 다른 품목으로 바꾸면 품목에 종속된 로트 선택은 초기화
    if (id !== itemId) {
      receiveForm.setValue("lotId", "");
      receiveForm.setValue("lotMode", "new");
    }
    setItemId(id);
    setStep("receive");
  };

  const runSearch = () => {
    const { category, spec } = itemForm.getValues();
    search.mutate(
      { files: picker.files, category, spec },
      {
        onError: (e) =>
          toast.error(e.response?.data.message ?? "이미지 검색에 실패했습니다."),
      },
    );
  };

  /** 사진 1장 업로드. 대표 사진이면 품목 imageUrl 도 갱신한다. */
  const uploadOne = async (id: string, imageId: string) => {
    const target = images.find((i) => i.id === imageId);
    if (!target) return false;
    picker.setStatus(imageId, "uploading");
    try {
      const saved = await upload.mutateAsync({ itemId: id, file: target.file });
      if (imageId === primaryId && saved) {
        await updateItem.mutateAsync({ id, values: { imageUrl: saved.path } });
      }
      picker.setStatus(imageId, "done");
      return true;
    } catch (e) {
      const message = axios.isAxiosError<{ message?: string }>(e)
        ? e.response?.data?.message
        : undefined;
      picker.setStatus(imageId, "error", message ?? "업로드 실패");
      return false;
    }
  };

  const proceedWithNewItem = (id: string) => {
    selectItem(id);
  };

  const uploadAll = async (id: string) => {
    uploadingRef.current = true;
    const ordered = [...images]
      .filter((i) => i.status !== "done")
      .sort((a, b) => Number(b.id === primaryId) - Number(a.id === primaryId));
    let allOk = true;
    for (const image of ordered) {
      allOk = (await uploadOne(id, image.id)) && allOk;
    }
    uploadingRef.current = false;
    if (allOk) proceedWithNewItem(id);
    else
      toast.error(
        "일부 사진 업로드에 실패했습니다. 재시도하거나 건너뛰고 입고를 계속할 수 있습니다.",
      );
  };

  const retry = async (imageId: string) => {
    if (!createdId) return;
    uploadingRef.current = true;
    const ok = await uploadOne(createdId, imageId);
    uploadingRef.current = false;
    const othersDone = images.every(
      (i) => i.id === imageId || i.status === "done",
    );
    if (ok && othersDone) proceedWithNewItem(createdId);
  };

  const onCreateItem = async (values: ItemFormValues) => {
    let id = createdId;
    if (!id) {
      const res = await createItem.mutateAsync(toCreateItemRequest(values));
      id = res.data.data?.id ?? null;
      if (!id) return;
      setCreatedId(id);
    }
    await uploadAll(id);
  };

  const onDone = (r: StockInResultResponse) => {
    setResult(r);
    setStep("done");
    draft.clearDraft();
  };

  const restart = () => {
    picker.clearImages();
    itemForm.reset(EMPTY_ITEM_FORM);
    receiveForm.reset(emptyStockInForm());
    search.reset();
    setItemId(null);
    setCreatedId(null);
    setResult(null);
    setStep("find");
  };

  const photoSection = (
    <ImagePicker
      images={images}
      primaryId={primaryId}
      disabled={isBusy || locked}
      onAdd={picker.addFiles}
      onRemove={picker.removeImage}
      onSelectPrimary={picker.setPrimaryId}
      onRetry={locked ? retry : undefined}
    />
  );

  return (
    <div className='flex max-w-4xl flex-col gap-6'>
      <StepIndicator step={step} />

      {(step === "find" || step === "new-item") && (
        <Card>
          <CardHeader>
            <CardTitle>1. 입고할 자재 사진으로 품목 찾기</CardTitle>
          </CardHeader>
          <CardContent className='flex flex-col gap-4'>
            {photoSection}

            {step === "find" && (
              <>
                <div className='grid gap-4 sm:grid-cols-2'>
                  <Field>
                    <FieldLabel htmlFor='stock-in-category'>카테고리</FieldLabel>
                    <NativeSelect
                      id='stock-in-category'
                      className='w-full'
                      {...itemForm.register("category")}
                    >
                      <option value=''>전체</option>
                      {ITEM_CATEGORY_VALUES.map((c) => (
                        <option key={c} value={c}>
                          {ITEM_CATEGORY_LABELS[c]}
                        </option>
                      ))}
                    </NativeSelect>
                  </Field>
                  <Field>
                    <FieldLabel htmlFor='stock-in-spec'>
                      규격 (차이 비교용, 선택)
                    </FieldLabel>
                    <Input
                      id='stock-in-spec'
                      placeholder='예: 폭 150cm / 면 100%'
                      {...itemForm.register("spec")}
                    />
                  </Field>
                </div>
                <div className='flex flex-wrap gap-2'>
                  <Button
                    type='button'
                    disabled={images.length === 0 || search.isPending}
                    onClick={runSearch}
                  >
                    {search.isPending ? "검색 중…" : "비슷한 품목 찾기"}
                  </Button>
                  <Button
                    type='button'
                    variant='outline'
                    onClick={() => setStep("new-item")}
                  >
                    새 품목 등록
                  </Button>
                </div>
                {images.length === 0 && (
                  <p className='text-xs text-muted-foreground'>
                    사진을 첨부하면 비슷한 기존 품목을 추천해 드립니다.
                  </p>
                )}

                {search.data && (
                  <ItemRecommendations
                    data={search.data}
                    onSelectItem={(r: ItemImageSearchResult) =>
                      selectItem(r.itemId)
                    }
                    onCreateNew={() => setStep("new-item")}
                  />
                )}
              </>
            )}

            {step === "new-item" && (
              <form
                onSubmit={itemForm.handleSubmit(onCreateItem)}
                className='flex flex-col gap-4 border-t pt-4'
              >
                <h3 className='font-semibold'>새 품목 정보</h3>
                <ItemFormFields form={itemForm} />
                <div className='flex flex-wrap gap-2'>
                  <Button type='submit' disabled={isBusy}>
                    {isBusy
                      ? "저장 중…"
                      : locked
                        ? "사진 업로드 계속"
                        : "품목 등록 후 입고 입력"}
                  </Button>
                  {!locked && (
                    <Button
                      type='button'
                      variant='outline'
                      disabled={isBusy}
                      onClick={() => setStep("find")}
                    >
                      추천 목록으로 돌아가기
                    </Button>
                  )}
                  {locked && hasFailed && (
                    <Button
                      type='button'
                      variant='outline'
                      disabled={isBusy}
                      onClick={() => createdId && proceedWithNewItem(createdId)}
                    >
                      건너뛰고 입고 계속
                    </Button>
                  )}
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      )}

      {step === "receive" && itemLoading && (
        <p className='text-muted-foreground'>품목을 불러오는 중…</p>
      )}
      {step === "receive" && (itemError || (!itemLoading && !item)) && (
        <div className='flex flex-col items-start gap-2'>
          <p className='text-destructive'>품목을 찾을 수 없습니다.</p>
          <Button variant='outline' onClick={() => setStep("find")}>
            품목 다시 선택
          </Button>
        </div>
      )}
      {step === "receive" && item && (
        <ReceiveForm
          item={item}
          form={receiveForm}
          onChangeItem={() => setStep("find")}
          onDone={onDone}
        />
      )}

      {step === "done" && result && (
        <Card>
          <CardHeader>
            <CardTitle>입고가 확정되었습니다</CardTitle>
          </CardHeader>
          <CardContent className='flex flex-col gap-4'>
            <dl className='grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2'>
              <dt className='text-muted-foreground'>입고 번호</dt>
              <dd className='font-medium'>{result.stockIn.inNo}</dd>
              <dt className='text-muted-foreground'>로트 번호</dt>
              <dd className='font-medium'>{result.lot.lotNo}</dd>
              <dt className='text-muted-foreground'>입고 수량</dt>
              <dd>
                {formatQuantity(result.stockIn.quantity)} {item?.baseUnit}
              </dd>
              <dt className='text-muted-foreground'>로트 보유 수량</dt>
              <dd>
                {formatQuantity(result.movement.quantityBefore)} →{" "}
                {formatQuantity(result.movement.quantityAfter)} {item?.baseUnit}
              </dd>
              <dt className='text-muted-foreground'>보관 위치</dt>
              <dd>
                {result.lot.location
                  ? `${result.lot.location.code} · ${result.lot.location.name}`
                  : "-"}
              </dd>
            </dl>
            <div className='flex flex-wrap gap-2'>
              <Button onClick={restart}>계속 입고</Button>
              <Button asChild variant='outline'>
                <Link href='/inventory'>재고 보기</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
